import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  parseSignedReceptionistContext,
  saveReceptionistBookingContext,
  type SignedReceptionistContext,
} from "./receptionistBooking";

type BookingSlot = {
  entryId: number;
  date: string;
  startTime: string;
  durationMinutes: number;
};
type BookingOffering = {
  id: number;
  slideId: number;
  title: string;
  description: string;
  classification: "group" | "one-on-one";
  slots: BookingSlot[];
};
type BookingAvailability = { timeZone: string; offerings: BookingOffering[] };
type ContactDetails = { name?: string; email?: string; phone?: string };
type BookingConfirmation = {
  message: string;
  conversationId?: string | null;
  cancellationUrl?: string;
  cancelToken?: string;
  canceled?: boolean;
  entryId?: number;
  date?: string;
};

const fieldClass =
  "mt-1 w-full rounded-md px-3 py-2 text-[14px] outline-none focus:ring-2 focus:ring-[color:var(--theme-accent)]";
const fieldStyle = {
  color: "var(--theme-recept-title)",
  background: "var(--theme-recept-thread-bg)",
  border: "1px solid var(--theme-recept-border-strong)",
};

function readableDate(date: string) {
  const [year, month, day] = date.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: "UTC",
  }).format(new Date(Date.UTC(year, month - 1, day, 12)));
}
function readableTime(time: string) {
  const [hour, minute] = time.split(":").map(Number);
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric", minute: "2-digit", timeZone: "UTC",
  }).format(new Date(Date.UTC(2020, 0, 1, hour, minute)));
}
export function savedConfirmation(slug?: string, conversationId?: string | null): BookingConfirmation | null {
  if (!slug || !conversationId) return null;
  try {
    const saved = window.sessionStorage.getItem(`ic-receptionist-booking-confirmation:${slug}`);
    const value = saved ? JSON.parse(saved) : null;
    return value && value.conversationId === conversationId && typeof value.message === "string" ? value : null;
  } catch {
    return null;
  }
}

export default function AppointmentBooking({
  slug,
  initialContact,
  context,
  isPreview,
  onClose,
  onConversationConfirmed,
}: {
  slug?: string;
  initialContact: ContactDetails;
  context: SignedReceptionistContext | null;
  isPreview: boolean;
  onClose: () => void;
  onConversationConfirmed?: (context: SignedReceptionistContext) => void;
}) {
  const [availability, setAvailability] = useState<BookingAvailability | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [offeringId, setOfferingId] = useState<number | null>(null);
  const [slot, setSlot] = useState<BookingSlot | null>(null);
  const [name, setName] = useState(initialContact.name ?? "");
  const [email, setEmail] = useState(initialContact.email ?? "");
  const [phone, setPhone] = useState(initialContact.phone ?? "");
  const [acknowledged, setAcknowledged] = useState(false);
  const [phase, setPhase] = useState<"details" | "review" | "sending" | "confirmed">(
    () => savedConfirmation(slug, context?.conversationId) ? "confirmed" : "details",
  );
  const [error, setError] = useState("");
  const [confirmation, setConfirmation] = useState<BookingConfirmation | null>(() => savedConfirmation(slug, context?.conversationId));
  const [rescheduling, setRescheduling] = useState(false);
  const bookingContextRef = useRef(context);
  useEffect(() => {
    const current = bookingContextRef.current;
    if (context?.historyToken && (!current || (
      current.conversationId === context.conversationId &&
      current.transcript.every((message, index) =>
        message.role === context.transcript[index]?.role &&
        message.content === context.transcript[index]?.content)
    ))) bookingContextRef.current = context;
  }, [context]);
  const [canceling, setCanceling] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const offering = useMemo(
    () => availability?.offerings.find((item) => item.id === offeringId) ?? null,
    [availability, offeringId],
  );

  const loadAvailability = useCallback(async () => {
    if (!slug) {
      setAvailability({ timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, offerings: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError("");
    try {
      const response = await fetch(`/api/ic/receptionist/booking/${encodeURIComponent(slug)}`);
      const body = await response.json();
      if (!response.ok || !body || !Array.isArray(body.offerings)) {
        throw new Error(typeof body?.error === "string" ? body.error : "Availability is temporarily unavailable.");
      }
      setAvailability(body as BookingAvailability);
      setOfferingId((current) => current && body.offerings.some((item: BookingOffering) => item.id === current)
        ? current
        : body.offerings.length === 1 ? body.offerings[0].id : null);
      setSlot(null);
    } catch (cause) {
      setLoadError(cause instanceof Error ? cause.message : "Availability is temporarily unavailable.");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { void loadAvailability(); }, [loadAvailability]);

  const submit = async () => {
    if (!slot || !slug || !availability || !bookingContextRef.current?.historyToken) return;
    setPhase("sending");
    setError("");
    try {
      const response = await fetch(`/api/ic/class-schedule/${encodeURIComponent(slug)}/rsvp${rescheduling ? "/reschedule" : ""}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          entryId: slot.entryId,
          date: slot.date,
          ...(rescheduling ? {
            sourceEntryId: confirmation?.entryId,
            sourceDate: confirmation?.date,
            cancelToken: confirmation?.cancelToken,
            confirmed: true,
            expectedStartTime: slot.startTime,
            expectedDurationMinutes: slot.durationMinutes,
          } : {
            name: name.trim(),
            email: email.trim(),
            phone: phone.trim(),
            termsPrivacyAcknowledged: acknowledged,
          }),
          ...(bookingContextRef.current?.conversationId ? { conversationId: bookingContextRef.current.conversationId } : {}),
          historyToken: bookingContextRef.current?.historyToken,
          messages: bookingContextRef.current?.transcript,
        }),
      });
      const body = await response.json().catch(() => null);
      if (response.status === 409) {
        if (body?.code === "booking_conversation_conflict") {
          setPhase("review");
          setError(body?.error || "This conversation could not be verified for booking. Your details are unchanged; please continue in this conversation or contact the team.");
          return;
        }
        setPhase("details");
        setError(body?.error || "That time is no longer available. Availability has been refreshed.");
        await loadAvailability();
        return;
      }
      if (response.status === 400 && body?.code === "booking_history_token_invalid") {
        setPhase("review");
        setError(body?.error || "The secure conversation proof has expired. Your details are saved here. Continue chatting and reopen booking to refresh it.");
        return;
      }
      if (!response.ok) throw new Error(body?.error || "We couldn't complete this booking. Please try again.");
      const signedChatContext = parseSignedReceptionistContext(slug, body?.chatContext);
      if (signedChatContext) {
        bookingContextRef.current = signedChatContext;
        saveReceptionistBookingContext(signedChatContext);
        onConversationConfirmed?.(signedChatContext);
      }
      if (typeof body?.cancelToken === "string") {
        try {
          if (rescheduling && confirmation?.entryId && confirmation.date) {
            window.localStorage.removeItem(`ic-rsvp-cancel:${slug}:${confirmation.entryId}:${confirmation.date}`);
          }
          window.localStorage.setItem(
            `ic-rsvp-cancel:${slug}:${slot.entryId}:${slot.date}`,
            body.cancelToken,
          );
        } catch {
          // Keep the confirmation even if local storage is blocked.
        }
      }
      const nextConfirmation = {
        conversationId: bookingContextRef.current?.conversationId,
        message: body?.confirmation?.message || body?.message || "Your appointment is confirmed.",
        cancellationUrl: typeof body?.cancellationUrl === "string"
          ? body.cancellationUrl
          : typeof body?.cancelUrl === "string" ? body.cancelUrl : undefined,
        cancelToken: typeof body?.cancelToken === "string" ? body.cancelToken : undefined,
        entryId: slot.entryId,
        date: slot.date,
      };
      setConfirmation(nextConfirmation);
      try {
        window.sessionStorage.setItem(
          `ic-receptionist-booking-confirmation:${slug}`,
          JSON.stringify(nextConfirmation),
        );
      } catch {
        // Confirmation remains visible until this chat is closed.
      }
      setRescheduling(false);
      setPhase("confirmed");
    } catch (cause) {
      setPhase("review");
      setError(cause instanceof Error ? cause.message : "Network error. Please try again.");
    }
  };

  const cancelBooking = async () => {
    if (!slug || !confirmation?.entryId || !confirmation.date || !confirmation.cancelToken || canceling) return;
    setCanceling(true);
    setCancelError("");
    try {
      const response = await fetch(`/api/ic/class-schedule/${encodeURIComponent(slug)}/rsvp/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          entryId: confirmation.entryId,
          date: confirmation.date,
          cancelToken: confirmation.cancelToken,
        }),
      });
      const body = await response.json().catch(() => null);
      if (!response.ok) throw new Error(body?.error || "We couldn't cancel this booking. Please try again.");
      const next = { ...confirmation, canceled: true };
      setConfirmation(next);
      try {
        window.localStorage.removeItem(`ic-rsvp-cancel:${slug}:${confirmation.entryId}:${confirmation.date}`);
        window.sessionStorage.setItem(`ic-receptionist-booking-confirmation:${slug}`, JSON.stringify(next));
      } catch { /* Best-effort cleanup. */ }
    } catch (cause) {
      setCancelError(cause instanceof Error ? cause.message : "Network error. Please try again.");
    } finally {
      setCanceling(false);
    }
  };

  const canContinue = Boolean(
    name.trim() &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) &&
    (!phone.trim() || phone.replace(/\D/g, "").length >= 7) &&
    acknowledged &&
    slot &&
    bookingContextRef.current?.historyToken,
  );

  if (phase === "confirmed") {
    return (
      <section className="rounded-xl border p-4" style={{ borderColor: "var(--theme-accent)", background: "#fff" }} role="status" aria-live="polite" data-testid="booking-confirmed">
        <h3 className="text-[15px] font-semibold" style={{ color: "var(--theme-recept-title)" }}>Booking Confirmed</h3>
        <p className="mt-2 text-[13px] leading-relaxed" style={{ color: "var(--theme-recept-body)" }}>{confirmation?.message}</p>
        {confirmation?.canceled && <p className="mt-2 text-[13px]" role="status">This booking has been canceled.</p>}
        {confirmation?.cancellationUrl && (
          <a className="mt-2 inline-block text-[13px] font-medium underline" style={{ color: "var(--theme-accent)" }} href={confirmation.cancellationUrl}>Manage or cancel this booking</a>
        )}
        {confirmation?.cancelToken && !confirmation.canceled && (
          <button type="button" disabled={canceling} className="mt-2 block text-[13px] font-medium underline disabled:opacity-50" style={{ color: "var(--theme-accent)" }} onClick={() => {
            setRescheduling(true);
            setPhase("details");
            setError("");
            void loadAvailability();
          }}>Reschedule This Appointment</button>
        )}
        {confirmation?.cancelToken && !confirmation.canceled && (
          <button type="button" disabled={canceling} className="mt-2 block text-[13px] font-medium underline disabled:opacity-50" style={{ color: "var(--theme-recept-muted)" }} onClick={() => void cancelBooking()}>
            {canceling ? <span className="inline-flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" />Canceling booking…</span> : "Cancel this booking"}
          </button>
        )}
        {cancelError && <p className="mt-2 text-[12px] text-red-700" role="alert">{cancelError}</p>}
        <button type="button" className="mt-3 block text-[13px] font-medium underline" style={{ color: "var(--theme-accent)" }} onClick={() => {
          setConfirmation(null);
          setRescheduling(false);
          setPhase("details");
          setAcknowledged(false);
          setError("");
          try { window.sessionStorage.removeItem(`ic-receptionist-booking-confirmation:${slug}`); } catch { /* best effort */ }
          void loadAvailability();
        }}>Book Another Appointment</button>
        <button type="button" className="mt-3 block text-[13px] font-medium underline" style={{ color: "var(--theme-recept-muted)" }} onClick={onClose}>Back to chat</button>
      </section>
    );
  }

  if (loading) {
    return <div className="space-y-3 rounded-xl border p-4" aria-busy="true" aria-label="Loading appointment availability" data-testid="booking-loading">
      <p className="flex items-center gap-2 text-[13px]"><Loader2 className="h-4 w-4 animate-spin" />Loading available times…</p>
      <div className="h-4 w-2/5 animate-pulse rounded bg-stone-200" />
      <div className="h-10 animate-pulse rounded bg-stone-100" />
      <div className="h-10 animate-pulse rounded bg-stone-100" />
    </div>;
  }
  if (loadError) return <div className="rounded-xl border p-4" role="alert">
    <p className="text-[13px]">{loadError}</p>
    <button type="button" className="mt-2 text-[13px] font-semibold underline" onClick={() => void loadAvailability()}>Try again</button>
    <button type="button" className="ml-4 text-[13px] underline" onClick={onClose}>Back to chat</button>
  </div>;
  if (isPreview) return <div className="rounded-xl border p-4" data-testid="booking-preview">
    <p className="text-[13px] font-medium">Design preview only. This action will not reserve a time.</p>
    <button type="button" className="mt-2 text-[13px] underline" onClick={onClose}>Back to chat</button>
  </div>;
  if (!availability?.offerings.length) return <div className="rounded-xl border p-4">
    <p className="text-[13px]">There are no bookable appointments available right now.</p>
    <button type="button" className="mt-2 text-[13px] underline" onClick={onClose}>Back to chat</button>
  </div>;

  const selectedSummary = offering && slot
    ? `${offering.title} · ${readableDate(slot.date)} at ${readableTime(slot.startTime)} · ${slot.durationMinutes} min · ${availability.timeZone}`
    : "";

  return (
    <section className="space-y-3 rounded-xl border p-4" style={{ borderColor: "var(--theme-recept-border)", background: "#fff" }} aria-label={rescheduling ? "Reschedule an appointment" : "Book an appointment"} data-testid="appointment-booking">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-[15px] font-semibold" style={{ color: "var(--theme-recept-title)" }}>{rescheduling ? "Reschedule Appointment" : "Book an Appointment"}</h3>
          <p className="mt-1 text-[12px]" style={{ color: "var(--theme-recept-muted)" }}>Choose an offering and available time.</p>
        </div>
        <button type="button" disabled={phase === "sending"} className="text-[12px] underline disabled:opacity-50" onClick={onClose}>Keep chatting</button>
      </div>
      {rescheduling && <div className="space-y-2 text-[12px]">
        <p>Current booking: {confirmation?.message}</p>
        <p>Your original booking stays reserved unless you confirm a successful move. Existing follow-up tasks and their dates will not change.</p>
        <button type="button" disabled={phase === "sending"} className="underline disabled:opacity-50" onClick={() => { setRescheduling(false); setPhase("confirmed"); setError(""); }}>Keep Original Booking</button>
      </div>}
      {!context?.historyToken && <p role="alert" className="text-[12px] text-red-700">Continue the conversation to securely confirm a booking.</p>}
      {availability.offerings.length > 1 && <label className="block text-[13px] font-medium">
        Offering
        <select disabled={phase === "sending"} className={fieldClass} style={fieldStyle} value={offeringId ?? ""} onChange={(event) => { setOfferingId(Number(event.target.value) || null); setSlot(null); setPhase("details"); }}>
          <option value="">Choose an offering</option>
          {availability.offerings.map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}
        </select>
      </label>}
      {offering && <>
        {offering.description && <p className="text-[12px] leading-relaxed" style={{ color: "var(--theme-recept-muted)" }}>{offering.description}</p>}
        <fieldset>
          <legend className="mb-2 text-[13px] font-medium">Available times</legend>
          {offering.slots.length === 0
            ? <p className="text-[12px]">No times are currently available for this offering.</p>
            : <div className="grid gap-2">
              {offering.slots.map((item) => {
                const label = `${readableDate(item.date)} · ${readableTime(item.startTime)} · ${item.durationMinutes} min · ${availability.timeZone}`;
                return <button key={`${item.entryId}-${item.date}-${item.startTime}`} type="button" disabled={phase === "sending"} aria-pressed={slot === item} onClick={() => { setSlot(item); setPhase("details"); }} className="rounded-md border px-3 py-2 text-left text-[12px] focus-visible:outline focus-visible:outline-2" style={{ borderColor: slot === item ? "var(--theme-accent)" : "var(--theme-recept-border)", background: slot === item ? "color-mix(in srgb, var(--theme-accent) 8%, white)" : "white", color: "var(--theme-recept-title)" }}>{label}</button>;
              })}
            </div>}
        </fieldset>
      </>}
      {offering && slot && phase === "details" && rescheduling && <button type="button" disabled={!bookingContextRef.current?.historyToken} onClick={() => { setError(""); setPhase("review"); }} className="rounded-md px-3 py-2 text-[13px] font-semibold disabled:opacity-50" style={{ background: "var(--theme-accent)", color: "#fff" }}>Review Change</button>}
      {offering && slot && phase === "details" && !rescheduling && <div className="space-y-2">
        <label className="block text-[13px] font-medium">Full name<input className={fieldClass} style={fieldStyle} autoComplete="name" value={name} onChange={(event) => setName(event.target.value)} /></label>
        <label className="block text-[13px] font-medium">Email<input className={fieldClass} style={fieldStyle} type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label>
        <label className="block text-[13px] font-medium">Phone (optional)<input className={fieldClass} style={fieldStyle} type="tel" autoComplete="tel" value={phone} onChange={(event) => setPhone(event.target.value)} /></label>
        <label className="flex items-start gap-2 text-[12px] leading-relaxed">
          <input type="checkbox" className="mt-1" checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} />
          <span>I acknowledge the <a className="underline" href={`/p/${encodeURIComponent(slug ?? "")}/terms`} target="_blank" rel="noreferrer">Terms</a> and <a className="underline" href={`/p/${encodeURIComponent(slug ?? "")}/privacy`} target="_blank" rel="noreferrer">Privacy Policy</a>.</span>
        </label>
        <button type="button" disabled={!canContinue} onClick={() => { setError(""); setPhase("review"); }} className="rounded-md px-3 py-2 text-[13px] font-semibold disabled:opacity-50" style={{ background: "var(--theme-accent)", color: "#fff" }}>Review Booking</button>
      </div>}
      {(phase === "review" || phase === "sending") && <div className="rounded-lg p-3" style={{ background: "var(--theme-recept-thread-bg)" }}>
        <p className="text-[12px] font-semibold">{rescheduling ? "Review your appointment change" : "Review your booking"}</p>
        <p className="mt-1 text-[12px]">{selectedSummary}</p>
        {!rescheduling && <p className="mt-1 text-[12px]">{name.trim()} · {email.trim()}{phone.trim() ? ` · ${phone.trim()}` : ""}</p>}
        <div className="mt-3 flex flex-wrap gap-3">
          <button type="button" disabled={phase === "sending"} onClick={() => void submit()} className="rounded-md px-3 py-2 text-[13px] font-semibold disabled:opacity-50" style={{ background: "var(--theme-accent)", color: "#fff" }}>{phase === "sending" ? <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />{rescheduling ? "Rescheduling…" : "Booking…"}</span> : rescheduling ? "Confirm Reschedule" : "Confirm Booking"}</button>
          <button type="button" disabled={phase === "sending"} className="text-[13px] underline disabled:opacity-50" onClick={() => setPhase("details")}>Edit details</button>
        </div>
      </div>}
      {error && <p role="alert" className="text-[12px] font-medium text-red-700">{error}</p>}
    </section>
  );
}
